import { useEffect, useState } from "react";
import { translate, type AppLocale } from "../i18n";
import { eraseOrderPrivacy, getOrderPrivacy, type JpShippingAddress, type OrderPrivacyResponse,
  type WesternShippingAddress } from "../lib/api";
import { formatAddressLines } from "../lib/address";
import { BRAND } from "../lib/brand";
import { flowCopy, pendingErasureReason } from "./flow-copy";
import { retainedCategoryLabel } from "./retained-category-copy";

export function OrderPrivacy({ token, locale }: { token: string; locale: AppLocale }) {
  const copy = flowCopy(locale);
  const [data, setData] = useState<OrderPrivacyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [eraseError, setEraseError] = useState(false);
  const [confirm, setConfirm] = useState(false);

  function addressLines(value: unknown): string[] | null {
    if (!value || typeof value !== "object") return null;
    const address = value as Record<string, unknown>;
    if (address.countryCode === "JP" && typeof address.prefecture === "string" &&
      typeof address.recipientName === "string" && typeof address.postalCode === "string") {
      return formatAddressLines(address as unknown as JpShippingAddress, locale);
    }
    if (typeof address.countryCode === "string" && typeof address.line1 === "string" &&
      typeof address.recipientName === "string") {
      return formatAddressLines(address as unknown as WesternShippingAddress, locale);
    }
    return null;
  }

  useEffect(() => {
    let active = true;
    setLoading(true); setError(false); setData(null);
    void getOrderPrivacy(token).then(result => { if (active) setData(result); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);

  function download() {
    if (!data) return;
    const file = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(file);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `tinkr-order-data-${data.orderId}.json`;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async function erase() {
    if (!data || erasing) return;
    setErasing(true); setEraseError(false);
    try {
      const result = await eraseOrderPrivacy(token);
      setData(current => current && ({ ...current, email: result.status === "completed" ? null : current.email,
        shipping: result.status === "completed" ? null : current.shipping,
        records: result.status === "completed" ? [] : current.records,
        retainedCategories: result.retainedCategories,
        erasure: { status: result.status, requestedAt: new Date().toISOString() } }));
      setConfirm(false);
    } catch { setEraseError(true); } finally { setErasing(false); }
  }

  if (loading) return <main className="co-state"><div className="co-spinner" aria-hidden /><p>{copy.privacyTitle}</p></main>;
  if (error || !data) return <main className="co-state"><h1>{copy.privacyTitle}</h1><p>{copy.privacyFailed}</p>
    <a className="co-btn-secondary" href={`mailto:${BRAND.supportEmail}`}>{copy.support}</a></main>;
  return <main className="co-page co-privacy">
    <section className="co-card"><h1 className="co-card-title">{copy.privacyTitle}</h1><p>{data.erasure.status === "completed" ? copy.erasureIntro : copy.privacyIntro}</p>
      {data.email && <div className="co-price-row"><span>{copy.email}</span><strong>{data.email}</strong></div>}
      {data.shipping != null && <div className="co-privacy-address">
        <h2>{translate(locale, "review.shipping.title")}</h2>
        {addressLines(data.shipping)?.map((line, index) => <div key={index}>{line}</div>)
          ?? <pre className="co-privacy-json">{JSON.stringify(data.shipping, null, 2)}</pre>}
      </div>}
      {data.erasure.status !== "completed" && <button className="co-btn-secondary" type="button" onClick={download}>{copy.download}</button>}
    </section>
    <section className="co-card"><h2 className="co-card-title">{copy.erasureTitle}</h2><p>{copy.erasureIntro}</p>
      <p>{pendingErasureReason(locale)}</p>
      {data.retainedCategories.length > 0 && <><h3>{copy.retained}</h3><ul>
        {data.retainedCategories.map(category => <li key={category}>{retainedCategoryLabel(locale, category)}</li>)}
      </ul></>}
      {data.erasure.status === "completed" ? <p role="status">{copy.eraseDone}</p>
        : data.erasure.status === "pending" ? <p role="status">{copy.erasePending}</p>
          : confirm ? <div className="co-handoff-actions">
              <button className="co-cta" type="button" disabled={erasing} onClick={() => void erase()}>{copy.eraseConfirm}</button>
              <button className="co-btn-secondary" type="button" disabled={erasing} onClick={() => setConfirm(false)}>{copy.eraseCancel}</button>
            </div> : <button className="co-btn-secondary" type="button" onClick={() => setConfirm(true)}>{copy.erase}</button>}
      {eraseError && <p role="alert" className="co-handoff-error">{copy.eraseFailed}</p>}
    </section>
    {data.erasure.status !== "completed" && <a className="co-btn-secondary" href={`/o/${encodeURIComponent(token)}?lang=${locale}`}>{copy.backOrder}</a>}
    <a className="co-btn-secondary" href={`mailto:${BRAND.supportEmail}`}>{copy.support}</a>
  </main>;
}
